import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { useAppDispatch, useAppSelector } from 'utils/hooks';
import { FILESTACK_API_KEY } from 'utils/globals';
import { roomIsUserRoom } from 'utils/type_guards';
import { renderModal } from 'utils/modal_util';

import Button from 'components/shared/Button';
import Modal from 'components/shared/Modal';
import NavigationTile from 'components/shared/NavigationTile';
import FSPicker from 'components/shared/FSPicker';

import roomsModule, {
  WallPreviewUserRoom,
  WallPreviewSampleRoom,
} from 'domains/wall_preview_rooms';

import session from 'domains/session';

import type { PickerResponse } from 'filestack-js';

type ChooseRoomModalProps = {
  lastFocusedElementId: string;
  onFilestackUploadDone: (result: PickerResponse) => void;
  onSelectPreview: (room: WallPreviewUserRoom | WallPreviewSampleRoom) => void;
  onEditRoom: (room: WallPreviewUserRoom) => void;
  onClose: () => void;
};

const options = {
  maxFiles: 50,
  maxSize: 1024 * 1024 * 1024, // 1GB
  accept: ['image/jpeg', 'image/png', 'image/tiff'],
  fromSources: ['local_file_system', 'facebook', 'instagram', 'googledrive', 'dropbox'],
  concurrency: 3, // <number> - Max number of concurrent parts uploading (chunks of files, not whole files).
};

function ChooseRoomModal(props: ChooseRoomModalProps) {
  const { lastFocusedElementId, onFilestackUploadDone, onSelectPreview, onEditRoom, onClose } =
    props;

  const [showUploadDialog, setShowUploadDialog] = useState(false);

  const dispatch = useAppDispatch();
  const isLoggedIn = useAppSelector(session.selectors.isLoggedIn);
  const allRoomData = useAppSelector(roomsModule.selectors.selectAll);
  const userRoomUploads = useAppSelector(roomsModule.selectors.userRooms);

  const [roomsFilter, setRoomsFilter] = useState(() => {
    if (userRoomUploads.length > 0) {
      return 'user';
    }
    return 'sample';
  });

  function handleDeleteRoom(id: number) {
    dispatch(roomsModule.actions.deleteUserRoom.pending(id));
  }

  function handleFilestackUploadDone(blobs: PickerResponse) {
    setShowUploadDialog(false);
    onFilestackUploadDone(blobs);
  }

  useEffect(() => {
    if (!isLoggedIn) {
      setRoomsFilter('sample');
    }
  }, [isLoggedIn]);

  const roomThumbnails = allRoomData
    .filter((room) => room.type === roomsFilter)
    .map((room) => {
      const menuItems = roomIsUserRoom(room)
        ? [
            {
              label: 'Edit',
              icon: <FontAwesomeIcon icon="edit" />,
              click: () => onEditRoom(room),
            },
            {
              label: 'Delete',
              icon: <FontAwesomeIcon icon="trash-alt" />,
              click: () => handleDeleteRoom(room.id),
            },
          ]
        : [];

      return (
        <li key={room.previewSrc}>
          <NavigationTile
            image={room.previewSrc}
            details={`Wall approximately ${room.displaySize}`}
            item={room}
            menuItems={menuItems}
            name={room.description}
            clickHandler={() => onSelectPreview(room)}
          />
        </li>
      );
    });

  const primaryButtons = roomsFilter === 'user' && (
    <Button
      type="outline"
      onClick={() => setShowUploadDialog(true)}
      className="add-room-button"
      disabled={!isLoggedIn}
    >
      <FontAwesomeIcon icon="cloud-upload-alt" size="lg" />
      Upload your room
    </Button>
  );

  const secondaryButtons = (
    <React.Fragment>
      <Button
        type="bare"
        onClick={() => setRoomsFilter('user')}
        className={roomsFilter === 'user' ? 'active' : ''}
      >
        Your Rooms
      </Button>
      <Button
        type="bare"
        onClick={() => setRoomsFilter('sample')}
        className={`unbutton ${roomsFilter === 'sample' ? 'active' : ''}`}
      >
        Sample Rooms
      </Button>
    </React.Fragment>
  );

  return (
    <React.Fragment>
      <Modal
        title="Choose Your Room"
        labelledBy="select-wall-preview-modal"
        modalClass={['choose-room-modal']}
        lastFocusedElementId={lastFocusedElementId}
        onClose={onClose}
        primaryFooterGroup={primaryButtons || null}
        secondaryFooterGroup={secondaryButtons}
      >
        <React.Fragment>
          {!isLoggedIn && roomsFilter === 'user' && (
            <p className="modal-message">Log in to upload photos of your own room</p>
          )}
        </React.Fragment>
        <React.Fragment>
          {roomThumbnails.length === 0 && isLoggedIn && (
            <p className="modal-message">
              Upload a photo of your room or choose a sample room to get started!
            </p>
          )}
        </React.Fragment>
        <ul className="modal-list-items">{roomThumbnails}</ul>
      </Modal>

      {showUploadDialog &&
        renderModal(
          <Modal
            onClose={() => setShowUploadDialog(false)}
            lastFocusedElementId="upload-photos-trigger"
            labelledBy="upload photos"
            title="Upload Photos"
            modalClass={['upload-photo-picker']}
          >
            <FSPicker
              apikey={FILESTACK_API_KEY}
              pickerOptions={options}
              onUploadDone={handleFilestackUploadDone}
            />
          </Modal>
        )}
    </React.Fragment>
  );
}

export default ChooseRoomModal;
